import { useEffect, useState, useRef } from "react";

import "./search-input.scss";
import TextInput from "../../../shared/components/text-input/text-input";
import { IEnumerationOption } from "../../../shared/definitions/datatypes";

export interface ISearchInputProps {
  label: string;
  inputKey: string;
  required: boolean;
  readonly?: boolean;
  data: IEnumerationOption[];
  selectedOption: string;
  onFocusEmpty?: boolean;
  handleOnChange(newValue: IEnumerationOption | undefined): void;
}

interface OutsideClickHandlerProps {
  ref: React.RefObject<HTMLElement>;
  handler: () => void;
}

const useOutsideClick = ({ ref, handler }: OutsideClickHandlerProps) => {
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (ref.current && !ref.current.contains(event.target as Node)) {
        handler();
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [ref, handler]);
};

const SearchInput = ({ 
  inputKey,
  onFocusEmpty,
  required,
  readonly = false,
  label,
  data,
  selectedOption: activeOption,
  handleOnChange
}: ISearchInputProps) => {
  const [inputVal, setInputVal] = useState("");
  const [selectedOption, setSelectedOption] = useState<IEnumerationOption | undefined>(undefined);
  const [showList, setShowList] = useState(false);
  const [filteredOptions, setFilteredOptions] = useState<IEnumerationOption[]>([]);
  const [allOptions, setAllOptions] = useState<IEnumerationOption[]>([]);

  const resultRef = useRef<HTMLDivElement | null>(null);

  useOutsideClick({
    ref: resultRef,
    handler: () => setShowList(false)
  });

  useEffect(() => {
    if (allOptions.length && activeOption) {
      const option = allOptions.find(option => option.id === activeOption);
      setSelectedOption(option);
      setInputVal(option?.name || "");
    }
  }, [activeOption, allOptions]);

  useEffect(() => {
    if (data && data.length !== 0) {
      setAllOptions(data);
    }
  }, [data]);

  useEffect(() => {
    if (allOptions.length !== 0) {
      const results = allOptions.filter((opt) =>
        opt.name.toLowerCase().includes(inputVal.toLowerCase())
      );
      setFilteredOptions(results);
    } else {
      setFilteredOptions([]);
    }
  }, [inputVal, allOptions, data]);

  const handleInputChange = (newVal: string) => {
    setInputVal(newVal);
    setShowList(true);

    // if user provided identical option names and did not click on the list item to select one,but typed in manually, then select first found match
    const checkManualTyping = filteredOptions.find((option) => newVal === option.name);
    if (checkManualTyping) {   
      setSelectedOption(checkManualTyping);
      handleOnChange(checkManualTyping);
    } else {
      setSelectedOption({id: "", name:""});
      handleOnChange(undefined);
    }
  };

  const handleKeyDown = (event:React.KeyboardEvent) => {
    if (event.key === "Enter") {
      onActionHandler();
    }
  };

  const onActionHandler = () => {
    if (!selectedOption) {
      setInputVal("");
      handleOnChange(undefined);
    } else {
      setInputVal(selectedOption.name);
      handleOnChange(selectedOption);
    }
  };

  const onFocusHandler = () => {
    setShowList(true);
    if (filteredOptions.length > 0) {
      onFocusEmpty && setInputVal("");
    }
  };

  return <div ref={resultRef} className="searchInput">
    <TextInput
      type={"text"}
      id={inputKey}
      name={inputKey}
      inputKey={inputKey}
      label={label}
      disabled={readonly}
      required={required}
      placeholder="Type to search"
      handleOnFocus={onFocusHandler}
      onKeyDown={handleKeyDown}
      handleOnBlur={onActionHandler}
      handleOnChange={handleInputChange}
      value={inputVal}
      controlled={true}
      errorMsg={`Please provide a ${label || "value"}`}
    />
    {showList && filteredOptions.length !== 0 && (
      <div className="searchInput-resultWrapper">
        <div className={`searchInput-result ${filteredOptions.length < 8? "searchInput-rounded" : ""}`}>
          <ul>
            {filteredOptions.map((option, i) => (
              <li
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => {
                  setInputVal(option.name);
                  handleInputChange(option.name);
                  setShowList(false);
                }}
                key={i}
              >
                {option.name}
              </li>
            ))}
          </ul>
        </div>
      </div>
    )}
  </div>;
};

export default SearchInput;
